import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { apiService } from "../../utils/apiService";
import { mapUserDataFromBackend } from "../../utils/userDataMapper";
import { Card, CardContent, CardHeader } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  UserPlus,
  Mail,
  Phone,
  Shield,
  Eye,
  EyeOff,
  Save,
  X,
  UserX,
  Users,
  UserCheck,
  User as UserIcon,
  Edit,
  Trash2,
  Search,
  QrCode,
  KeyRound,
} from "lucide-react";
import { AddUserDialog } from "./AddUserDialog";
import { Role, User } from "../../types";
import { ALL_ROLES, ROLE_LABELS, effectiveRoles, userHasPermission, userHasRole } from "../../utils/accessControl";
import { syncCachedUsers } from "../../utils/dataCache";
import Swal from "sweetalert2";
import { formatPhilippinePhoneInput, normalizePhilippinePhone } from "../../utils/phoneNumber";

interface UserManagementProps {
  users: User[];
  setUsers: (users: User[]) => void;
  currentUser: User;
}

const swalCustomClasses = {
  container: "swal-container",
  popup: "swal-popup !max-w-md swal-actions-right",
  title: "swal-title",
  htmlContainer: "swal-content",
  confirmButton: "swal-confirm-button",
  cancelButton: "swal-cancel-button",
};

export function UserManagement({
  users,
  setUsers,
  currentUser,
}: UserManagementProps) {
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showSecureIds, setShowSecureIds] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [verifyPassword, setVerifyPassword] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [passwordUser, setPasswordUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showInactiveModal, setShowInactiveModal] = useState(false);
  const [inactiveUsers, setInactiveUsers] = useState<User[]>([]);
  const [loadingInactive, setLoadingInactive] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState<string>("all");

  const [displayCount, setDisplayCount] = useState(5);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  // State for viewing full-size QR code
  const [selectedQrUrl, setSelectedQrUrl] = useState<string | null>(null);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  const canManageUsers = userHasPermission(currentUser, "user.manage_standard");
  const canManagePrivilegedRoles = userHasRole(currentUser, "sysadmin");
  const tableCheckboxClass =
    "border-slate-300 bg-white/90 dark:border-white/40 dark:bg-white/10 data-[state=checked]:bg-white data-[state=checked]:text-[#103055] dark:data-[state=checked]:bg-white dark:data-[state=checked]:text-slate-900";

  // Fetch inactive users when modal opens
  useEffect(() => {
    if (!showInactiveModal || !canManageUsers) return;

    const fetchInactive = async () => {
      setLoadingInactive(true);
      try {
        const response = await apiService.getInactiveUsers();
        if (response.data) {
          setInactiveUsers(response.data.map(mapUserDataFromBackend));
        }
      } catch (err) {
        console.error("Failed to load inactive users", err);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Could not load inactive users.",
          timer: 2200,
          customClass: swalCustomClasses,
        });
      } finally {
        setLoadingInactive(false);
      }
    };
    fetchInactive();
  }, [showInactiveModal, canManageUsers]);

  // Secure ID visibility toggle (requires password verification)
  const handleToggleSecureIds = () => {
    if (showSecureIds) {
      setShowSecureIds(false);
      return;
    }

    setVerifyPassword("");
    setVerifyError("");
    setShowPasswordModal(true);
  };

  const handleVerifyPassword = async () => {
    if (!verifyPassword.trim()) {
      setVerifyError("Password is required");
      return;
    }

    setIsVerifying(true);
    setVerifyError("");

    try {
      await apiService.verifyPassword(verifyPassword);
      setShowSecureIds(true);
      setShowPasswordModal(false);

      Swal.fire({
        icon: "success",
        title: "Access Granted",
        text: "Secure IDs are now visible",
        timer: 1400,
        showConfirmButton: false,
        customClass: swalCustomClasses,
      });
    } catch {
      setVerifyError("Incorrect password. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleUserCreated = (newUser: User) => {
    const nextUsers = [mapUserDataFromBackend(newUser), ...users];
    setUsers(nextUsers);
    syncCachedUsers(nextUsers);
    setShowAddUserDialog(false);
    Swal.fire({
      icon: "success",
      title: "User created",
      text: `${newUser.name} can now sign in with the initial password.`,
      timer: 2200,
      showConfirmButton: false,
      customClass: swalCustomClasses,
    });
  };

  const handleEditUser = (user: User) => {
    setEditingUser({ ...user });
    setShowEditModal(true);
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    if (effectiveRoles(editingUser).length === 0) {
      await Swal.fire({
        icon: "error",
        title: "Role required",
        text: "Select at least one role for this account.",
        customClass: swalCustomClasses,
      });
      return;
    }

    const emailRegex = /^\S+@\S+\.\S+$/;
    const gcashRegex = /^09\d{9}$/;

    if (!editingUser.name.trim()) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Full name is required",
        customClass: swalCustomClasses,
      });
      return;
    }

    if (!editingUser.email.trim()) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Email is required",
        customClass: swalCustomClasses,
      });
      return;
    }

    if (!editingUser.phone?.trim()) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Phone number is required",
        customClass: swalCustomClasses,
      });
      return;
    }

    if (!emailRegex.test(editingUser.email ?? "")) {
      await Swal.fire({
        icon: "error",
        title: "Invalid Email",
        text: "Please enter a valid email address (example@company.com)",
        customClass: swalCustomClasses,
      });
      return;
    }

    let normalizedPhone: string | undefined;
    try {
      normalizedPhone = normalizePhilippinePhone(editingUser.phone || "");
    } catch (phoneError) {
      await Swal.fire({
        icon: "error",
        title: "Invalid Phone",
        text: phoneError instanceof Error ? phoneError.message : "Enter a valid Philippine mobile number",
        customClass: swalCustomClasses,
      });
      return;
    }

    if (editingUser.gcashNumber && !gcashRegex.test(editingUser.gcashNumber)) {
      await Swal.fire({
        icon: "error",
        title: "Invalid GCash Number",
        text: "GCash must start with 09 and be 11 digits",
        customClass: swalCustomClasses,
      });
      return;
    }

    if (editingUser.gcashNumber && editingUser.gcashNumber.length !== 11) {
      await Swal.fire({
        icon: "error",
        title: "Invalid GCash Length",
        text: "GCash number must be exactly 11 digits",
        customClass: swalCustomClasses,
      });
      return;
    }

    const original = users.find((u) => u.id === editingUser.id);
    if (original && JSON.stringify(original) === JSON.stringify(editingUser)) {
      Swal.fire({
        icon: "info",
        title: "No changes",
        text: "Nothing was modified.",
        timer: 1400,
        showConfirmButton: false,
        customClass: swalCustomClasses,
      });
      setShowEditModal(false);
      setEditingUser(null);
      return;
    }

    const result = await Swal.fire({
      title: "Save changes?",
      text: "This will update the user's information.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, save",
      cancelButtonText: "Cancel",
      customClass: swalCustomClasses,
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Saving...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
      customClass: swalCustomClasses,
    });

    try {
        const originalRoles = original ? [...effectiveRoles(original)].sort() : [];
        const nextRoles = [...effectiveRoles(editingUser)].sort();
        const rolesChanged = JSON.stringify(originalRoles) !== JSON.stringify(nextRoles);
        const payload: Partial<User> = {
          name: editingUser.name.trim(),
          email: editingUser.email.trim().toLowerCase(),
          phone: normalizedPhone || null,
          school: editingUser.school?.trim() || "",
          gcashNumber: editingUser.gcashNumber?.trim() || null,
        };
        if (rolesChanged) payload.roles = nextRoles;

        const updateResponse = await apiService.updateUser(editingUser.id, payload);
        if (updateResponse.data?.reauthentication_required) {
          Swal.close();
          await Swal.fire({
            icon: "success",
            title: "Access Updated",
            text: "Your access roles changed. Please sign in again.",
            confirmButtonText: "Return to login",
            customClass: swalCustomClasses,
          });
          await apiService.logout();
          window.location.reload();
          return;
        }

        const res = await apiService.getUsers();
        if (res.data) {
          const nextUsers = res.data.map(mapUserDataFromBackend);
          setUsers(nextUsers);
          syncCachedUsers(nextUsers);
        }

        Swal.close();
        Swal.fire({
          icon: "success",
          title: "User Updated",
          timer: 1600,
          showConfirmButton: false,
          customClass: swalCustomClasses,
        });

        setShowEditModal(false);
        setEditingUser(null);
      } catch (err) {
        Swal.close();
        Swal.fire({
          icon: "error",
          title: "Update Failed",
          text: err instanceof Error ? err.message : "Could not save changes.",
          customClass: swalCustomClasses,
        });
      }
  };

  const openPasswordChange = (user: User) => {
    setPasswordUser(user);
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
  };

  const handleChangePassword = async () => {
    if (!passwordUser) return;
    const strongPassword = newPassword.length >= 12
      && /[a-z]/.test(newPassword)
      && /[A-Z]/.test(newPassword)
      && /\d/.test(newPassword)
      && /[^A-Za-z0-9]/.test(newPassword);
    if (!strongPassword || newPassword !== confirmPassword) {
      await Swal.fire({
        icon: "error",
        title: "Password not accepted",
        text: newPassword !== confirmPassword
          ? "The password confirmation does not match."
          : "Use at least 12 characters with uppercase, lowercase, number, and symbol.",
        customClass: swalCustomClasses,
      });
      return;
    }

    setIsChangingPassword(true);
    try {
      const response = await apiService.changeUserPassword(passwordUser.id, newPassword);
      const requiresLogin = response.data?.reauthentication_required === true;
      setPasswordUser(null);
      await Swal.fire({
        icon: "success",
        title: "Password Changed",
        text: requiresLogin
          ? "Your password changed. Please sign in again."
          : "The user can now sign in with the new password.",
        confirmButtonText: requiresLogin ? "Return to login" : "Done",
        customClass: swalCustomClasses,
      });
      if (requiresLogin) {
        await apiService.logout();
        window.location.reload();
      }
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Password Change Failed",
        text: error instanceof Error ? error.message : "Unable to change the password.",
        customClass: swalCustomClasses,
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    if (userId === currentUser.id) {
      Swal.fire({
        icon: "warning",
        title: "Cannot deactivate yourself",
        text: "Please ask another admin to do this.",
        customClass: swalCustomClasses,
      });
      return;
    }

    const result = await Swal.fire({
      title: "Deactivate user?",
      text: "User will no longer be able to log in.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, deactivate",
      cancelButtonText: "Cancel",
      customClass: swalCustomClasses,
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Deactivating...",
      text: "Please wait a moment.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
      customClass: swalCustomClasses,
    });

    setTimeout(async () => {
      try {
        await apiService.makeUserInactive(userId);
        const [activeRes, inactiveRes] = await Promise.all([
          apiService.getUsers(),
          apiService.getInactiveUsers(),
        ]);
        if (activeRes.data) {
          const nextUsers = activeRes.data.map(mapUserDataFromBackend);
          setUsers(nextUsers);
          syncCachedUsers(nextUsers);
        }
        if (inactiveRes.data) {
          setInactiveUsers(inactiveRes.data.map(mapUserDataFromBackend));
        }

        Swal.close();
        Swal.fire({
          icon: "success",
          title: "User Deactivated",
          timer: 1600,
          showConfirmButton: false,
          customClass: swalCustomClasses,
        });
      } catch (err: any) {
        Swal.close();
        console.error("Deactivation failed:", err);
        Swal.fire({
          icon: "error",
          title: "Deactivation Failed",
          text: err?.response?.data?.message || "Server error",
          customClass: swalCustomClasses,
        });
      }
    }, 1800);
  };

  const handleDeactivateSelected = async () => {
    const targetIds = selectedUserIds.filter((id) => id !== currentUser.id);
    if (targetIds.length === 0) return;

    const result = await Swal.fire({
      title: "Deactivate selected users?",
      text: `This will deactivate ${targetIds.length} user(s).`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, deactivate",
      cancelButtonText: "Cancel",
      customClass: swalCustomClasses,
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Deactivating...",
      text: "Please wait a moment.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
      customClass: swalCustomClasses,
    });

    setTimeout(async () => {
      try {
        await Promise.all(
          targetIds.map((userId) => apiService.makeUserInactive(userId)),
        );
        const [activeRes, inactiveRes] = await Promise.all([
          apiService.getUsers(),
          apiService.getInactiveUsers(),
        ]);
        if (activeRes.data) {
          const nextUsers = activeRes.data.map(mapUserDataFromBackend);
          setUsers(nextUsers);
          syncCachedUsers(nextUsers);
        }
        if (inactiveRes.data) {
          setInactiveUsers(inactiveRes.data.map(mapUserDataFromBackend));
        }

        setSelectedUserIds([]);
        Swal.close();
        Swal.fire({
          icon: "success",
          title: "Users Deactivated",
          timer: 1600,
          showConfirmButton: false,
          customClass: swalCustomClasses,
        });
      } catch (err: any) {
        Swal.close();
        console.error("Bulk deactivation failed:", err);
        Swal.fire({
          icon: "error",
          title: "Deactivation Failed",
          text: err?.response?.data?.message || "Server error",
          customClass: swalCustomClasses,
        });
      }
    }, 1800);
  };

  const handleRestoreUser = async (userId: string) => {
    const result = await Swal.fire({
      title: "Restore user?",
      text: "User will be able to log in again.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, restore",
      cancelButtonText: "Cancel",
      customClass: swalCustomClasses,
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Restoring...",
      text: "Please wait a moment.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
      customClass: swalCustomClasses,
    });

    setTimeout(async () => {
      try {
        await apiService.makeUserActive(userId);
        const [activeRes, inactiveRes] = await Promise.all([
          apiService.getUsers(),
          apiService.getInactiveUsers(),
        ]);
        if (activeRes.data) {
          const nextUsers = activeRes.data.map(mapUserDataFromBackend);
          setUsers(nextUsers);
          syncCachedUsers(nextUsers);
        }
        if (inactiveRes.data) {
          setInactiveUsers(inactiveRes.data.map(mapUserDataFromBackend));
        }

        Swal.close();
        Swal.fire({
          icon: "success",
          title: "User Restored",
          text: "Account is now active again.",
          timer: 1800,
          showConfirmButton: false,
          customClass: swalCustomClasses,
        });
      } catch (err: any) {
        Swal.close();
        console.error("Restore failed:", err);
        Swal.fire({
          icon: "error",
          title: "Restore Failed",
          text: err?.response?.data?.message || "Server error",
          customClass: swalCustomClasses,
        });
      }
    }, 1800);
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingUser(null);
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "admin":
        return "destructive";
      case "supervisor":
        return "default";
      case "fabricator":
        return "secondary";
      default:
        return "outline";
    }
  };

  // Filtering logic
  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredUsers = useMemo(() => {
    let result = users;

    if (selectedRole !== "all") {
      result = result.filter((user) => effectiveRoles(user).includes(selectedRole as Role));
    }

    if (normalizedSearch) {
      result = result.filter((user) => {
        const haystack = [
          user.name,
          user.email,
          ...effectiveRoles(user),
          user.school,
          user.employeeNumber,
          user.secureId,
          user.phone,
          user.gcashNumber,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(normalizedSearch);
      });
    }

    return result;
  }, [users, normalizedSearch, selectedRole]);

  const displayedUsers = useMemo(() => {
    return filteredUsers.slice(0, displayCount);
  }, [filteredUsers, displayCount]);

  const hasMore = displayCount < filteredUsers.length;

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    setTimeout(() => {
      setDisplayCount((prev) => prev + 5);
      setLoadingMore(false);
    }, 500);
  }, [hasMore, loadingMore]);

  useEffect(() => {
    setDisplayCount(5);
  }, [searchTerm, selectedRole]);

  useEffect(() => {
    if (!loadMoreTriggerRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    observerRef.current.observe(loadMoreTriggerRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [hasMore, loadingMore, loadMore]);

  useEffect(() => {
    const userIds = new Set(users.map((user) => user.id));
    setSelectedUserIds((prev) => prev.filter((id) => userIds.has(id)));
  }, [users]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between w-full">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <UserIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
            <h1 className="text-3xl font-bold tracking-tight">
              User Management
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Manage system users, roles and permissions
          </p>
        </div>

        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
          {canManageUsers && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInactiveModal(true)}
                className="gap-2 w-full sm:w-auto"
              >
                <UserX size={16} />
                Inactive Users
              </Button>

              <Button
                size="sm"
                onClick={() => setShowAddUserDialog(true)}
                className="gap-2 w-full sm:w-auto bg-primary text-primary-foreground hover:bg-secondary "
              >
                <UserPlus size={16} />
                Add New User
              </Button>
            </>
          )}
        </div>
      </div>

      <Card className="border border-border/60  rounded-[24px]">
        <CardHeader className="p-4 sm:p-6 pb-2 border-b border-border/60">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex-1">
              <div className="relative max-w-xl">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search user...."
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-muted/40 dark:bg-slate-800/60 border-border/60"
                />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">
                  Note:
                </span>{" "}
                Search by name, email, role, school, employee number, secure ID,
                phone, or GCash number.
              </p>
              <div className="mt-2 w-full sm:w-60">
                <Label className="text-sm mb-1.5 block">Filter by Role:</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="All roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    {ALL_ROLES.map((role) => (
                      <SelectItem key={role} value={role}>{ROLE_LABELS[role]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2 self-start">
              {canManageUsers && selectedUserIds.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full sm:w-auto"
                  onClick={handleDeactivateSelected}
                >
                  Deactivate {selectedUserIds.length}
                </Button>
              )}

              <Button
                variant={showSecureIds ? "destructive" : "outline"}
                size="sm"
                className="gap-2 w-full sm:w-auto"
                onClick={handleToggleSecureIds}
              >
                {showSecureIds ? <EyeOff size={16} /> : <Eye size={16} />}
                {showSecureIds ? "Hide" : "Show"} Secure IDs
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-3 sm:p-6 pt-2">
          {/* Table */}
          <div className="w-full rounded-md border border-border bg-card overflow-x-auto">
            <Table className="w-full min-w-[700px]">
              <TableHeader className="bg-muted">
                <TableRow className="hover:bg-transparent">
                  {canManageUsers && (
                    <TableHead className="w-12 text-center p-0">
                      <Checkbox
                        className={tableCheckboxClass}
                        checked={
                          displayedUsers.length > 0 &&
                          displayedUsers.every((u) =>
                            selectedUserIds.includes(u.id),
                          )
                            ? true
                            : displayedUsers.some((u) =>
                                selectedUserIds.includes(u.id),
                              )
                            ? "indeterminate"
                            : false
                        }
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedUserIds((prev) => [
                              ...new Set([
                                ...prev,
                                ...displayedUsers.map((u) => u.id),
                              ]),
                            ]);
                          } else {
                            setSelectedUserIds((prev) =>
                              prev.filter(
                                (id) =>
                                  !displayedUsers.some((u) => u.id === id),
                              ),
                            );
                          }
                        }}
                      />
                    </TableHead>
                  )}
                  <TableHead className="min-w-[220px]">Name & Email</TableHead>
                  <TableHead className="min-w-[110px]">Role</TableHead>
                  <TableHead className="min-w-[140px]">School</TableHead>
                  <TableHead className="min-w-[160px]">Contact</TableHead>
                  <TableHead className="min-w-[140px] text-center">
                    GCash QR
                  </TableHead>
                  {showSecureIds && (
                    <TableHead className="min-w-[160px]">Secure ID</TableHead>
                  )}
                  <TableHead className="min-w-[120px]">Employee #</TableHead>
                  {canManageUsers && (
                    <TableHead className="w-28 text-right">Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>

              <TableBody>
                {displayedUsers.length === 0 ? (
                  <TableRow className="odd:bg-muted/30 dark:odd:bg-slate-800/40">
                    <TableCell
                      colSpan={100}
                      className="h-48 text-center text-muted-foreground"
                    >
                      No users found matching your search or role filter
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedUsers.map((user) => (
                    <TableRow
                      key={user.id}
                      className="odd:bg-muted/30 even:bg-transparent dark:odd:bg-slate-800/50 dark:even:bg-slate-900/30 hover:bg-muted/60 dark:hover:bg-slate-800/70"
                    >
                      {canManageUsers && (
                        <TableCell className="text-center p-0">
                          <Checkbox
                            className={tableCheckboxClass}
                            checked={selectedUserIds.includes(user.id)}
                            disabled={user.id === currentUser.id}
                            onCheckedChange={(checked) => {
                              setSelectedUserIds((prev) =>
                                checked
                                  ? [...prev, user.id]
                                  : prev.filter((id) => id !== user.id),
                              );
                            }}
                          />
                        </TableCell>
                      )}

                      <TableCell>
                        <div className="font-medium">{user.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                          <Mail size={13} className="shrink-0" />
                          <span className="break-all">{user.email}</span>
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {effectiveRoles(user).map((role) => (
                            <Badge key={role} variant={getRoleBadgeVariant(user.role)} className="font-medium">
                              {ROLE_LABELS[role]}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>

                      <TableCell className="text-muted-foreground capitalize">
                        {user.school || "—"}
                      </TableCell>

                      <TableCell className="text-sm">
                        {user.phone && (
                          <div className="flex items-center gap-1.5">
                            <Phone
                              size={14}
                              className="shrink-0 text-muted-foreground"
                            />
                            {user.phone}
                          </div>
                        )}
                        {user.gcashNumber && (
                          <div className="text-xs text-muted-foreground/80 mt-1 italic">
                            GCash: {user.gcashNumber}
                          </div>
                        )}
                      </TableCell>

                      {/* GCash QR Display */}
                      <TableCell className="text-center">
                        {user.gcashQrUrl ? (
                          <div
                            className="inline-block cursor-pointer group relative"
                            onClick={() => setSelectedQrUrl(user.gcashQrUrl || null)}
                          >
                            <img
                              src={user.gcashQrUrl}
                              alt={`${user.name}'s GCash QR Code`}
                              className="h-14 w-14 rounded-sm object-cover border border-gray-300  group- group-hover:"
                              loading="lazy"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src =
                                  "/images/placeholder-qr.png"; // fallback image
                                (e.target as HTMLImageElement).alt =
                                  "QR code failed to load";
                              }}
                            />
                            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 rounded-sm flex items-center justify-center">
                              <QrCode className="h-6 w-6 text-white" />
                            </div>
                          </div>
                        ) : (
                          <div className="text-muted-foreground/60 text-xs italic flex flex-col items-center gap-1">
                            <QrCode className="h-5 w-5 opacity-40" />
                            <span>No QR</span>
                          </div>
                        )}
                      </TableCell>

                      {showSecureIds && (
                        <TableCell>
                          <code className="text-xs font-mono bg-muted px-2.5 py-1 rounded break-all">
                            {user.secureId || "—"}
                          </code>
                        </TableCell>
                      )}

                      <TableCell>
                        <code className="text-xs font-mono text-muted-foreground">
                          {user.employeeNumber || "—"}
                        </code>
                      </TableCell>

                      {canManageUsers && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleEditUser(user)}
                            >
                              <Edit size={16} />
                            </Button>

                            {canManagePrivilegedRoles && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openPasswordChange(user)}
                                aria-label={`Change password for ${user.name}`}
                              >
                                <KeyRound size={16} />
                              </Button>
                            )}

                            {user.id !== currentUser.id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleDeactivateUser(user.id)}
                              >
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Infinite scroll trigger */}
          {hasMore && (
            <div
              ref={loadMoreTriggerRef}
              className="py-10 flex justify-center items-center text-sm text-muted-foreground"
            >
              {loadingMore ? (
                <>
                  <div className="h-4 w-4 bg-current animate-spin mr-2" />
                  Loading more users...
                </>
              ) : (
                "Scroll down to load more"
              )}
            </div>
          )}

          {!hasMore && filteredUsers.length > 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              All matching users have been loaded
            </div>
          )}

          {filteredUsers.length === 0 && (
            <div className="py-12 text-center text-muted-foreground">
              No users match the current search or role filter
            </div>
          )}
        </CardContent>
      </Card>

      {/* Full-size QR Modal */}
      {selectedQrUrl && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85  p-4"
          onClick={() => setSelectedQrUrl(null)}
        >
          <div
            className="relative bg-card text-card-foreground border border-border rounded-xl shadow-xl max-w-4xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h3 className="text-xl font-semibold flex items-center gap-3">
                <QrCode className="h-6 w-6 text-foreground" />
                GCash QR Code
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedQrUrl(null)}
              >
                <X className="h-6 w-6" />
              </Button>
            </div>

            <div className="p-8 flex justify-center bg-muted/30">
              <img
                src={selectedQrUrl}
                alt="Full size GCash QR Code"
                className="max-h-[75vh] w-auto object-contain rounded-md border-2 border-border"
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "/images/placeholder-qr.png";
                }}
              />
            </div>

            <div className="p-4 border-t border-border text-center text-sm text-muted-foreground">
              Click outside or press ESC to close
            </div>
          </div>
        </div>
      )}

      {/* Password Verification Modal for Secure IDs */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60  p-4">
          <div className="bg-background/95  border  rounded-sm w-full max-w-md overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2.5">
                  <Shield className="h-5 w-5 text-primary" />
                  Security Check
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPasswordModal(false)}
                >
                  <X size={20} />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-1.5">
                Enter your password to view Secure IDs.
              </p>
            </div>

            <div className="p-6 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="verify-password">Password</Label>
                <Input
                  id="verify-password"
                  type="password"
                  autoFocus
                  value={verifyPassword}
                  onChange={(e) => {
                    setVerifyPassword(e.target.value);
                    setVerifyError("");
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleVerifyPassword()}
                  className={verifyError ? "border-destructive" : ""}
                />
                {verifyError && (
                  <p className="text-sm text-destructive mt-1.5">
                    {verifyError}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowPasswordModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleVerifyPassword}
                  disabled={isVerifying || !verifyPassword.trim()}
                >
                  {isVerifying ? (
                    <>
                      <div className="h-4 w-4 bg-current animate-spin mr-2" />
                      Verifying...
                    </>
                  ) : (
                    "Confirm"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inactive Users Modal */}
      {showInactiveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60  p-4">
          <div className="bg-background border rounded-sm  w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold flex items-center gap-2.5">
                  <UserX className="h-5 w-5 text-destructive" />
                  Inactive / Deactivated Users
                </h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowInactiveModal(false)}
                >
                  <X size={20} />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-1.5">
                These accounts are currently deactivated and cannot log in.
              </p>
            </div>

            <div className="flex-1 overflow-auto p-6">
              {loadingInactive ? (
                <div className="h-full flex items-center justify-center gap-3 text-muted-foreground">
                  <div className="h-4 w-4 bg-current animate-spin mr-2" />
                  Loading...
                </div>
              ) : inactiveUsers.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
                  <Users className="h-16 w-16 mb-4 opacity-50" />
                  <p className="text-lg font-medium">No inactive users found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10 border-b">
                    <TableRow>
                      <TableHead className="min-w-[200px]">
                        Name & Email
                      </TableHead>
                      <TableHead className="min-w-[110px]">Role</TableHead>
                      <TableHead className="min-w-[140px]">School</TableHead>
                      <TableHead className="min-w-[160px]">Contact</TableHead>
                      <TableHead className="min-w-[140px] text-center">
                        GCash QR
                      </TableHead>
                      {showSecureIds && (
                        <TableHead className="min-w-[160px]">
                          Secure ID
                        </TableHead>
                      )}
                      <TableHead className="min-w-[120px]">
                        Employee #
                      </TableHead>
                      <TableHead className="min-w-[140px] text-right">
                        Action
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inactiveUsers.map((user) => (
                      <TableRow key={user.id} className="hover:bg-muted/50">
                        <TableCell>
                          <div className="font-medium">{user.name}</div>
                          <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                            <Mail size={13} />
                            <span className="break-all">{user.email}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getRoleBadgeVariant(user.role)}>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground capitalize">
                          {user.school || "—"}
                        </TableCell>
                        <TableCell className="text-sm">
                          {user.phone && (
                            <div className="flex items-center gap-1.5">
                              <Phone size={14} className="shrink-0" />
                              {user.phone}
                            </div>
                          )}
                          {user.gcashNumber && (
                            <div className="text-xs text-muted-foreground/80 mt-1">
                              GCash: {user.gcashNumber}
                            </div>
                          )}
                        </TableCell>

                        {/* GCash QR in Inactive Modal */}
                        <TableCell className="text-center">
                          {user.gcashQrUrl ? (
                            <div
                              className="inline-block cursor-pointer group relative"
                              onClick={() => setSelectedQrUrl(user.gcashQrUrl || null)}
                            >
                              <img
                                src={user.gcashQrUrl}
                                alt={`${user.name}'s GCash QR`}
                                className="h-14 w-14 rounded-sm object-cover border border-gray-300  group-"
                                loading="lazy"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src =
                                    "/images/placeholder-qr.png";
                                }}
                              />
                              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 rounded-sm flex items-center justify-center">
                                <QrCode className="h-6 w-6 text-white" />
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground/60 text-xs italic">
                              No QR
                            </span>
                          )}
                        </TableCell>

                        {showSecureIds && (
                          <TableCell>
                            <code className="text-xs font-mono bg-muted px-2 py-1 rounded break-all">
                              {user.secureId || "—"}
                            </code>
                          </TableCell>
                        )}
                        <TableCell>
                          <code className="text-xs font-mono text-muted-foreground">
                            {user.employeeNumber || "—"}
                          </code>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleRestoreUser(user.id)}
                          >
                            <UserCheck size={16} className="mr-1.5" />
                            Restore
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60  p-4">
          <div className="bg-background border rounded-sm  w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-background z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Edit User</h2>
                <Button variant="ghost" size="icon" onClick={handleCancelEdit}>
                  <X size={20} />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Update user details below
              </p>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={editingUser.name || ""}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, name: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    minLength={1}
                    maxLength={50}
                    value={editingUser.email || ""}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, email: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    inputMode="tel"
                    maxLength={13}
                    value={editingUser.phone || ""}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, phone: formatPhilippinePhoneInput(e.target.value) })
                    }
                    placeholder="+639XXXXXXXXX"
                  />
                </div>

                <div className="space-y-2">
                  <Label>GCash Number</Label>
                  <Input
                    inputMode="tel"
                    maxLength={13}
                    placeholder="+639XXXXXXXXX or 09XXXXXXXXX"
                    value={editingUser.gcashNumber || ""}
                    onChange={(e) =>
                      setEditingUser({
                        ...editingUser,
                        gcashNumber: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>School / Institution</Label>
                  <Input
                    minLength={1}
                    maxLength={45}
                    value={editingUser.school || ""}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, school: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-3 md:col-span-2">
                  <Label>Roles</Label>
                  <div className="grid gap-3 rounded-md border p-4 sm:grid-cols-2 lg:grid-cols-3">
                    {ALL_ROLES.map((role) => {
                      const privileged = role === "sysadmin" || role === "executive";
                      const checked = effectiveRoles(editingUser).includes(role);
                      return (
                        <label key={role} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={checked}
                            disabled={privileged && !canManagePrivilegedRoles}
                            onCheckedChange={(value) => {
                              const currentRoles = effectiveRoles(editingUser);
                              const roles = value
                                ? [...new Set([...currentRoles, role])]
                                : currentRoles.filter((item) => item !== role);
                              setEditingUser({ ...editingUser, roles });
                            }}
                          />
                          {ROLE_LABELS[role]}
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Access is the union of selected roles. At least one role is required.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Secure ID</Label>
                  <Input
                    value={editingUser.secureId || ""}
                    disabled
                    readOnly
                  />
                  <p className="text-xs text-muted-foreground">Generated by the system and cannot be edited.</p>
                </div>

                <div className="space-y-2">
                  <Label>Employee #</Label>
                  <Input
                    value={editingUser.employeeNumber || ""}
                    disabled
                    readOnly
                  />
                  <p className="text-xs text-muted-foreground">Generated by the system and cannot be edited.</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6">
                <Button
                  variant="outline"
                  onClick={handleCancelEdit}
                  className="w-full sm:w-auto"
                >
                  <X size={16} className="mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveUser}
                  className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-secondary"
                >
                  <Save size={16} className="mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddUserDialog && (
        <AddUserDialog
          currentUser={currentUser}
          onCreated={handleUserCreated}
          onClose={() => setShowAddUserDialog(false)}
        />
      )}

      {passwordUser && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="change-password-title"
            className="w-full max-w-md rounded-xl border border-border bg-background shadow-xl"
          >
            <div className="flex items-start justify-between border-b p-5">
              <div>
                <h2 id="change-password-title" className="text-lg font-semibold">Change user password</h2>
                <p className="mt-1 text-sm text-muted-foreground">Set the password requested by {passwordUser.name}.</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setPasswordUser(null)} aria-label="Close">
                <X size={18} />
              </Button>
            </div>
            <div className="space-y-4 p-5">
              <div className="space-y-2">
                <Label htmlFor="managed-user-password">New password</Label>
                <div className="relative">
                  <Input
                    id="managed-user-password"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    className="pr-11"
                    autoComplete="new-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2"
                    onClick={() => setShowNewPassword((visible) => !visible)}
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                  >
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="managed-user-password-confirmation">Confirm password</Label>
                <Input
                  id="managed-user-password-confirmation"
                  type={showNewPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Use at least 12 characters with uppercase, lowercase, number, and symbol. Existing sessions will be revoked.
              </p>
              <div className="flex justify-end gap-3 border-t pt-4">
                <Button variant="outline" onClick={() => setPasswordUser(null)} disabled={isChangingPassword}>Cancel</Button>
                <Button onClick={handleChangePassword} disabled={isChangingPassword || !newPassword || !confirmPassword}>
                  <KeyRound size={16} />
                  {isChangingPassword ? "Changing..." : "Change password"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!canManageUsers && (
        <Card className="mt-10 border-dashed bg-muted/30">
          <CardContent className="py-12 text-center">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground/70 mb-4" />
            <h3 className="text-lg font-medium mb-2">
              Administrator Access Required
            </h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Only authorized administrators can manage users, view inactive
              accounts, create users, edit details, or reveal secure IDs.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
